const formatDateShort = (dateVal) => {
  if (!dateVal || dateVal === 'N/A') return 'N/A';
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return dateVal;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatFullName = (username) => {
  if (!username) return 'N/A';
  return username
    .replace(/[._]/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const generateTramiteExcelRow = (t, user, filterEstado = '') => {
  const v = t.datos_adicionales?.vehicleDetails?.vehiculo;
  const cc = v?.cilindraje || v?.datos_completos?.rna?.datos?.cilindraje;
  const datos = t.datos_adicionales || {};

  // Resolve specific fields to handle both nested 'tramites' and flat 'reports' queries
  const placa = datos.placa_referencia || t.plate || 'SIN PLACA';
  const propietario = datos.propietario || t.propietario || 'N/A';
  const identificacion = datos.documento || datos.identificacion || t.documento_propietario || 'N/A';
  const tipoTramite = t.tramite_type_name || t.tipo_tramite_name || 'N/A';
  const transito = t.transito_name || 'N/A';
  const concesionario = t.creador_concesionario_name || t.concesionario_name || 'N/A';
  
  // Format Concesionario name if address is present (useful for Superadmin views)
  let concesionarioFull = concesionario;
  if (t.creador_concesionario_name && t.creador_concesionario_address) {
    concesionarioFull = `${t.creador_concesionario_name} (${t.creador_concesionario_address})`;
  } else if (!t.creador_concesionario_name && t.creador_transito_name) {
    concesionarioFull = t.creador_transito_name;
  }

  const puntoVenta = t.punto_venta_name || datos.punto_venta_factura || datos.punto_venta_nombre || datos.punto_venta || datos.punto_de_venta || datos.sede || datos.sucursal || 'N/A';
  const vendedorFactura = datos.vendedor_factura || datos.asesor || formatFullName(t.creador_name || t.usuario_creador || 'N/A');

  const asesor = formatFullName(t.creador_name || t.usuario_creador || 'N/A');
  const rna = v?.datos_completos?.rna?.datos || {};
  const getValidChasis = () => {
    const candidates = [
      v?.nro_chasis,
      v?.nro_vin,
      rna.numeroChasis,
      rna.vin,
      datos.chasis,
      datos.vin
    ];
    for (const c of candidates) {
      if (c && String(c).trim().length >= 10 && !/^(1|LT25|IRINOS|RRIS|IS|N\/A)$/i.test(String(c).trim())) {
        return String(c).trim();
      }
    }
    for (const c of candidates) {
      if (c && String(c).trim() !== 'N/A' && String(c).trim() !== '') {
        return String(c).trim();
      }
    }
    return 'N/A';
  };
  const chasis = getValidChasis();
  const marca = v?.marca || rna.marcaVehiculo || datos.marca || 'N/A';
  const linea = v?.linea || rna.lineaVehiculo || datos.linea || 'N/A';
  const cilindraje = cc ? `${cc} cc` : (datos.cilindraje ? `${datos.cilindraje} cc` : 'N/A');
  const rawFactura = datos.factura_electronica || datos.numero_factura || datos.factura_numero || datos.num_factura || datos.factura || 'N/A';
  const cleanFacturaCode = (str) => {
    if (!str || str === 'N/A') return 'N/A';
    const s = String(str).trim();
    if (/^(NO|N\/A|SIN FACTURA|SIN_FACTURA|NINGUNA|NINGUNO|FALSE|UNDEFINED|NULL)$/i.test(s)) return 'N/A';
    if (s.toLowerCase().endsWith('.pdf') || s.toLowerCase().endsWith('.png') || s.toLowerCase().endsWith('.jpg') || /^uploads[\\/]/i.test(s) || /[\\/]uploads[\\/]/i.test(s) || s.toLowerCase().startsWith('reporte ')) return 'N/A';
    if (/^(202[0-9]|DE202[0-9]|HORAS202[0-9]|NIT\d*)$/i.test(s)) return 'N/A';
    const FORBIDDEN_YEARS = new Set(['2020', '2021', '2022', '2023', '2024', '2025', '2026', '2027']);
    const matchNum = s.match(/\b(?:FACTURA|FACT|FE|BILL|N[°ºo]?\.?\s*|NUM\.?\s*|NUMERO\s*|NRO\.?\s*)(\d{3,12})\b/i);
    if (matchNum && !FORBIDDEN_YEARS.has(matchNum[1])) return matchNum[1];
    const matchPrefix = s.match(/\b([A-Z]{2,6}-?\d{2,12})\b/i);
    if (matchPrefix) {
      const code = matchPrefix[1].replace(/[-\s]+/g, '').toUpperCase();
      const FORBIDDEN_CODES = new Set(['FACTURA', 'REPORTE', 'CUPL', 'SOAT', 'DOCUMENTO', 'ESTADO', 'PLACA', 'PDF', 'GUAMAL', 'NIT', 'NIT901', 'DE2026', 'HORAS2026', 'HORAS2025']);
      if (!FORBIDDEN_CODES.has(code) && !/202[0-9]$/.test(code)) return code;
    }
    const matchAnyNum = s.match(/\b(\d{4,12})\b/g);
    if (matchAnyNum) {
      for (const num of matchAnyNum) {
        if (!FORBIDDEN_YEARS.has(num) && !/^\d{13,}$/.test(num)) return num;
      }
    }
    if (s.length <= 20 && !/\s/.test(s) && !s.toLowerCase().startsWith('factura')) return s;
    return 'N/A';
  };
  const facturaElectronica = cleanFacturaCode(rawFactura);

  // Traducir estado para el Excel
  const getEstadoNombre = (estado) => {
    const estados = {
      'radicado': 'Radicado',
      'pre_asignado': 'Pre-asignado',
      'novedad_pendiente_soat': 'Pendiente SOAT',
      'novedad_soat_cargado': 'SOAT Cargado',
      'novedad_soat_vigente': 'SOAT Vigente',
      'novedad_pendiente_cupl': 'Pendiente CUPL',
      'novedad_cupl_generado': 'CUPL Generado',
      'novedad_cupl_pagado': 'CUPL Pagado',
      'tramite_ejecutado': 'Ejecutado',
      'rechazado': 'Rechazado'
    };
    return estados[estado] || estado;
  };

  const estadoNombre = getEstadoNombre(t.estado);

  // Determine Fecha based on state
  let fechaRaw = null;
  let fechaLabel = 'Fecha de Radicación';
  if (filterEstado && filterEstado !== 'todos' && filterEstado !== '') {
    if (filterEstado === 'tramite_ejecutado') {
      fechaRaw = t.fecha_ejecutado || t.updated_at || t.created_at;
      fechaLabel = 'Fecha de Ejecución';
    } else if (filterEstado === 'pre_asignado') {
      fechaRaw = t.fecha_preasignacion || t.updated_at || t.created_at;
      fechaLabel = 'Fecha de Preasignación';
    } else {
      fechaRaw = t.updated_at || t.created_at;
      fechaLabel = `Fecha de ${estadoNombre}`;
    }
  } else {
    // Si no hay filtro específico de estado, se usa SIEMPRE la fecha de radicación
    fechaRaw = t.created_at;
  }

  const fechaFormatted = formatDateShort(fechaRaw);

  // TRANSIAPP (Superadmin / Admin sin asignacion especifica)
  if (user?.role === 'superadmin' || user?.username === 'laura.avila' || (user?.role === 'admin' && !user?.transito_id && !user?.concesionario_id)) {
    return {
      'Chasis': chasis,
      'Placa': placa,
      [fechaLabel]: fechaFormatted,
      'Estado': estadoNombre,
      'Tipo de Trámite': tipoTramite,
      'Organismo de Tránsito': transito,
      'Concesionario': concesionarioFull,
      'Punto de Venta': puntoVenta,
      'Vendedor / Asesor': vendedorFactura,
      'Asesor / Creador': asesor,
      'Nombre Propietario': propietario,
      'Identificación': identificacion,
      'Marca': marca,
      'Línea': linea,
      'Cilindraje': cilindraje,
      'Factura Electrónica': facturaElectronica
    };
  }
  
  // TRANSITO
  if (user?.transito_id || user?.role === 'transito') {
    return {
      'Placa': placa,
      [fechaLabel]: fechaFormatted,
      'Estado': estadoNombre,
      'Tipo de Trámite': tipoTramite,
      'Nombre Propietario': propietario,
      'Identificación': identificacion,
      'Marca': marca,
      'Línea': linea,
      'Cilindraje': cilindraje,
      'Factura Electrónica': facturaElectronica
    };
  }

  // CONCESIONARIO
  if (user?.concesionario_id) {
    return {
      'Chasis': chasis,
      'Placa': placa,
      [fechaLabel]: fechaFormatted,
      'Estado': estadoNombre,
      'Tipo de Trámite': tipoTramite,
      'Organismo de Tránsito': transito,
      'Concesionario': concesionarioFull,
      'Punto de Venta': puntoVenta,
      'Vendedor / Asesor': vendedorFactura,
      'Asesor / Creador': asesor,
      'Nombre Propietario': propietario,
      'Identificación': identificacion,
      'Marca': marca,
      'Línea': linea,
      'Cilindraje': cilindraje,
      'Factura Electrónica': facturaElectronica
    };
  }

  // Fallback
  return {
    'Placa': placa,
    [fechaLabel]: fechaFormatted,
    'Estado': estadoNombre,
    'Tipo de Trámite': tipoTramite,
    'Punto de Venta': puntoVenta,
    'Vendedor / Asesor': vendedorFactura,
    'Nombre Propietario': propietario,
    'Identificación': identificacion,
    'Marca': marca,
    'Línea': linea,
    'Cilindraje': cilindraje,
    'Factura Electrónica': facturaElectronica
  };
};
